import { getEffectiveUsernameOverride } from "@features/me/queries";
import { portalApi } from "@services/api/portalApi";
import {
	queryOptions,
	useMutation,
	useQueryClient,
} from "@tanstack/react-query";
import type { ConversationMessage as LegacyConversationMessage } from "@godalming/api-types";
import type {
	StudentCallHistoryItem,
	StudentCommunicationTimelineItem,
} from "@features/student-communication-history/api";
import type { MessageFormat } from "./messageDocument";

export type ConversationMessageData = LegacyConversationMessage & {
	canEdit: boolean;
	isEdited: boolean;
	messageFormat?: MessageFormat;
	likes?: { initials: string; name: string }[];
	likedByMe?: boolean;
};
export type CommunicationSource =
	| "portal-email"
	| "outlook-email"
	| "threecx-call"
	| "teams-message";
export type ConversationCommunicationLink = {
	id: number;
	conversationId: number;
	source: CommunicationSource;
	sourceId: string;
	position: "occurred" | "linked";
	occurredAt: string;
	linkedAt: string;
	linkedBy: string;
	messageId: number | null;
};
export type ConversationTimelineItem =
	| {
			id: string;
			type: "message";
			sortAt: string;
			message: ConversationMessageData;
	  }
	| {
			id: string;
			type: "email" | "call" | "teams";
			sortAt: string;
			link: ConversationCommunicationLink;
			email: StudentCommunicationTimelineItem | null;
			call: StudentCallHistoryItem | null;
			message: ConversationMessageData | null;
	  };

export type LinkCommunicationRequest = {
	source: CommunicationSource;
	sourceId: string;
	position: "occurred" | "linked";
	message?: string;
	messageFormat?: MessageFormat;
	conversationIds?: number[];
	newConversation?: {
		typeCfCId: number;
		studentClassId: number | null;
		title: string;
		message?: string;
		messageFormat?: MessageFormat;
	};
};

export const conversationTimelineQueryOptions = (conversationId: string) => {
	const override = getEffectiveUsernameOverride();
	return queryOptions({
		queryKey: ["conversations", conversationId, "timeline", override],
		refetchInterval: 15000,
		refetchOnWindowFocus: "always",
		queryFn: async () =>
			(
				await portalApi.get<ConversationTimelineItem[]>(
					`/conversations/${conversationId}/timeline`,
					{ params: { usernameOverride: override ?? "" } },
				)
			).data,
	});
};

export const studentConversationLinksQueryOptions = (studentNo: string) =>
	queryOptions({
		queryKey: ["students", studentNo, "conversation-links"],
		queryFn: async () =>
			(
				await portalApi.get<ConversationCommunicationLink[]>(
					`/conversations/students/${studentNo}/communication-links`,
				)
			).data,
	});

export function useSaveCommunicationLink(studentNo: string) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (request: LinkCommunicationRequest) =>
			(
				await portalApi.post<ConversationCommunicationLink[]>(
					`/conversations/students/${studentNo}/communication-links`,
					request,
				)
			).data,
		onSuccess: async (links) => {
			await Promise.all([
				client.invalidateQueries({
					queryKey: ["students", studentNo, "conversations"],
				}),
				client.invalidateQueries(
					studentConversationLinksQueryOptions(studentNo),
				),
				...links.map((link) =>
					client.invalidateQueries({
						queryKey: ["conversations", String(link.conversationId)],
					}),
				),
			]);
		},
	});
}

export function useMessageChange(studentNo: string, conversationId: string) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (request: {
			id: number;
			message?: string;
			messageFormat?: MessageFormat;
		}) => {
			const url = `/conversations/${conversationId}/messages/${request.id}`;
			if (request.message === undefined) await portalApi.delete(url);
			else
				await portalApi.patch(url, {
					message: request.message,
					messageFormat: request.messageFormat,
				});
		},
		onSuccess: async () => {
			await Promise.all([
				client.invalidateQueries({
					queryKey: ["conversations", conversationId],
				}),
				client.invalidateQueries({
					queryKey: ["students", studentNo, "conversations"],
				}),
			]);
		},
	});
}

export function useUnlinkCommunication(
	studentNo: string,
	conversationId: string,
) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (linkId: number) => {
			await portalApi.delete(
				`/conversations/${conversationId}/communication-links/${linkId}`,
			);
		},
		onSuccess: async () => {
			await Promise.all([
				client.invalidateQueries(
					conversationTimelineQueryOptions(conversationId),
				),
				client.invalidateQueries(
					studentConversationLinksQueryOptions(studentNo),
				),
				client.invalidateQueries({
					queryKey: ["students", studentNo, "conversations"],
				}),
			]);
		},
	});
}

export function useMessageLike(conversationId: string) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (request: { messageId: number; isLiked: boolean }) => {
			const override = getEffectiveUsernameOverride();
			await portalApi.put(
				`/conversations/${conversationId}/messages/${request.messageId}/like`,
				{ isLiked: request.isLiked },
				{ params: { usernameOverride: override ?? "" } },
			);
		},
		onSuccess: async () => {
			await client.invalidateQueries({
				queryKey: ["conversations", conversationId],
			});
		},
	});
}

export function useCommunicationPlacement(
	studentNo: string,
	conversationId: string,
) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async (request: {
			linkId: number;
			position: "occurred" | "linked";
		}) => {
			const override = getEffectiveUsernameOverride();
			await portalApi.patch(
				`/conversations/${conversationId}/communication-links/${request.linkId}`,
				{ position: request.position },
				{ params: { usernameOverride: override ?? "" } },
			);
		},
		onSuccess: async () => {
			await Promise.all([
				client.invalidateQueries({
					queryKey: ["conversations", conversationId],
				}),
				client.invalidateQueries({
					queryKey: ["students", studentNo, "conversation-links"],
				}),
				client.invalidateQueries({
					queryKey: ["students", studentNo, "conversations"],
				}),
				client.invalidateQueries({ queryKey: ["favourite-conversations"] }),
			]);
		},
	});
}
